'use client';

import { useEffect, useRef, useCallback } from 'react';
import { cn } from '@/core/utils/cn';
import { AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  loading?: boolean;
}

const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function ConfirmDialog({
  id,
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading,
}: ConfirmDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      });
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Tab' && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose]);

  if (!open) return null;

  const titleId = id ? `${id}-title` : 'confirm-dialog-title';
  const descId = id ? `${id}-desc` : 'confirm-dialog-desc';

  const confirmStyles = {
    danger: 'bg-hms-red hover:bg-hms-red/80 text-white',
    warning: 'bg-hms-gold hover:bg-hms-goldLt text-black',
    default: 'bg-hms-gold hover:bg-hms-goldLt text-black',
  };

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        id={id}
        className="w-full max-w-sm bg-hms-card border border-hms-border rounded-2xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
            variant === 'danger' ? 'bg-hms-red/10' : 'bg-hms-gold/10'
          )}>
            <AlertTriangle className={cn('w-5 h-5', variant === 'danger' ? 'text-hms-red' : 'text-hms-gold')} />
          </div>
          <div>
            <h3 id={titleId} className="text-[15px] font-semibold text-hms-text">{title}</h3>
            <p id={descId} className="text-[13px] text-hms-textDim mt-1">{message}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            id={id ? `${id}-cancel-btn` : undefined}
            onClick={onClose}
            className="px-4 py-2 text-[13px] font-medium text-hms-textSub hover:text-hms-text transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            id={id ? `${id}-confirm-btn` : undefined}
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'px-4 py-2 text-[13px] font-semibold rounded-lg transition-colors',
              confirmStyles[variant],
              loading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {loading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
